import React, { useCallback, useEffect, useRef, useState } from "react";
import FormApi from "../../Api/FormApi";
import { FormResponseDto, UserData } from "../../Models/FormResponseDto";
import { Textarea, TextInput } from "flowbite-react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { useNavigate } from "react-router-dom";

interface EditFormResponsePageProps {
  responseId: number;
  onClose: () => void;
  bindSubmit: (submitFn: () => void) => void;
}

interface EditedAnswer {
  answer: string;
  question: string;
  price?: number;
  responseValueId: number;
  users?: UserData[];
  optionUsers?: { [optionValue: string]: UserData[] };
}

const EditFormResponsePage: React.FC<EditFormResponsePageProps> = ({
  responseId,
  onClose,
  bindSubmit,
}) => {
  const [responseData, setResponseData] = useState<FormResponseDto | null>(
    null
  );
  const [editedAnswers, setEditedAnswers] = useState<
    Record<number, EditedAnswer>
  >({});
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);

  const [formUser, setFormUser] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [updateLinkedProfile, setUpdateLinkedProfile] = useState(false);

  const formUserRef = useRef(formUser);
  const editedAnswersRef = useRef(editedAnswers);
  const updateLinkedProfileRef = useRef(updateLinkedProfile);

  useEffect(() => {
    formUserRef.current = formUser;
  }, [formUser]);

  useEffect(() => {
    updateLinkedProfileRef.current = updateLinkedProfile;
  }, [updateLinkedProfile]);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userModel, navigate]);

  useEffect(() => {
    if (!responseId) return;

    FormApi.getResponse(responseId)
      .then((res) => {
        setResponseData(res.data);
        const initialAnswers: Record<number, EditedAnswer> = {};
        res.data.answers?.forEach((a) => {
          initialAnswers[a.responseValueId] = {
            answer: a.answer,
            price: a.price,
            responseValueId: a.responseValueId,
            question: a.question,
            users: a.users,
            optionUsers: a.optionUsers,
          };
        });
        setEditedAnswers(initialAnswers);
        setFormUser({
          companyName: res.data.memberName || "",
          firstName: res.data.submittedByFirstName || "",
          lastName: res.data.submittedByLastName || "",
          email: res.data.submittedByEmail || "",
          phone: res.data.submittedByPhone || "",
        });
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load response.");
      })
      .finally(() => setLoading(false));
  }, [responseId]);

  const handleChange = (
    responseValueId: number,
    field: "answer" | "price",
    value: string | number
  ) => {
    setEditedAnswers((prev) => ({
      ...prev,
      [responseValueId]: {
        ...prev[responseValueId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!responseData) return;

    const updatedResponse = {
      ...responseData,
      answers: Object.values(editedAnswersRef.current).map((obj) => ({
        answer: obj.answer,
        price: obj.price ?? 0,
        responseValueId: obj.responseValueId,
        question: obj.question,
        users: obj.users,
        optionUsers: obj.optionUsers,
      })),
      memberName: formUserRef.current.companyName,
      submittedByFirstName: formUserRef.current.firstName,
      submittedByLastName: formUserRef.current.lastName,
      submittedByEmail: formUserRef.current.email,
      submittedByPhone: formUserRef.current.phone,
      updateLinkedProfile: updateLinkedProfileRef.current,
    };

    console.log("Final API Payload:", updatedResponse);

    try {
      await FormApi.updateResponse(responseData.responseId, updatedResponse);
      toast.success("Response updated successfully.");
      onClose();
    } catch (err: any) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 409
          ? "That booth is already reserved."
          : "Failed to update response.");
      toast.error(msg);
    }
  }, [responseData, editedAnswers, formUser, onClose]);

  const hasBound = useRef(false);
  useEffect(() => {
    if (!hasBound.current && responseData) {
      bindSubmit(() => handleSubmit());
      hasBound.current = true;
    }
  }, [bindSubmit, responseData, handleSubmit]);

  useEffect(() => {
    editedAnswersRef.current = editedAnswers;
  }, [editedAnswers]);

  if (loading) return <p>Loading...</p>;
  if (!responseData) return <p>No response found.</p>;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Edit Form Submission</h1>

      {responseData.filledByName ? (
        <p className="mb-4 text-sm text-gray-700">
          Filled by: <span className="font-semibold">{responseData.filledByName}</span>
        </p>
      ) : null}

      <h2 className="text-xl font-semibold mt-6 mb-2">Submitter Info</h2>
      <label className="flex items-center gap-2 mb-3 text-sm">
        <input
          type="checkbox"
          checked={updateLinkedProfile}
          onChange={(e) => setUpdateLinkedProfile(e.target.checked)}
        />
        Also update linked user profile (off by default — editing answers alone will not change their account)
      </label>
      <div className="grid grid-cols-1">
        <label className="font-semibold block mb-1 mt-1">Company Name</label>
        <TextInput
          className="ml-3"
          disabled={!updateLinkedProfile}
          value={formUser.companyName}
          onChange={(e) =>
            setFormUser({ ...formUser, companyName: e.target.value })
          }
        />
        <label className="font-semibold block mb-1 mt-1">First Name</label>
        <TextInput
          className="ml-3"
          disabled={!updateLinkedProfile}
          value={formUser.firstName}
          onChange={(e) =>
            setFormUser({ ...formUser, firstName: e.target.value })
          }
        />
        <label className="font-semibold block mb-1 mt-1">Last Name</label>
        <TextInput
          className="ml-3"
          disabled={!updateLinkedProfile}
          value={formUser.lastName}
          onChange={(e) =>
            setFormUser({ ...formUser, lastName: e.target.value })
          }
        />
        <label className="font-semibold block mb-1 mt-1">Email</label>
        <TextInput
          className="ml-3"
          type="email"
          disabled={!updateLinkedProfile}
          value={formUser.email}
          onChange={(e) => setFormUser({ ...formUser, email: e.target.value })}
        />
        <label className="font-semibold block mb-1 mt-1">Phone</label>
        <TextInput
          className="ml-3"
          disabled={!updateLinkedProfile}
          value={formUser.phone}
          onChange={(e) => setFormUser({ ...formUser, phone: e.target.value })}
        />
      </div>

      <div className="space-y-4 mt-4">
        {Object.values(editedAnswers).map((a) => (
          <div key={a.responseValueId} className="mb-4">
            <label className="font-semibold block mb-1 mt-1">
              {a.question}
            </label>
            <Textarea
              className="ml-3"
              value={a.answer}
              onChange={(e) =>
                handleChange(a.responseValueId, "answer", e.target.value)
              }
              rows={2}
              required
            />
            <label className="block mt-2 text-sm text-gray-600 ml-3">
              Price ($)
            </label>
            <TextInput
              className="ml-3"
              type="number"
              min={0}
              step="0.01"
              value={a.price ?? ""}
              onChange={(e) =>
                handleChange(
                  a.responseValueId,
                  "price",
                  parseFloat(e.target.value)
                )
              }
            />

            {/* Display users if present */}
            {a.users && a.users.length > 0 && (
              <div className="ml-3 mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm font-semibold mb-2">Participants:</p>
                <div className="space-y-1">
                  {a.users.map((user, idx) => (
                    <div key={idx} className="text-sm">
                      {idx + 1}. {user.firstName} {user.lastName}
                      {user.userType && ` - ${user.userType}`}
                      {user.price ? ` ($${user.price.toFixed(2)})` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Display option users if present */}
            {a.optionUsers && Object.keys(a.optionUsers).length > 0 && (
              <div className="ml-3 mt-3 space-y-2">
                {Object.entries(a.optionUsers).map(([optionValue, users]) => (
                  <div
                    key={optionValue}
                    className="p-3 bg-green-50 border border-green-200 rounded"
                  >
                    <p className="text-sm font-semibold mb-2">
                      Participants for "{optionValue}":
                    </p>
                    <div className="space-y-1">
                      {users.map((user, idx) => (
                        <div key={idx} className="text-sm">
                          {idx + 1}. {user.firstName} {user.lastName}
                          {user.userType && ` - ${user.userType}`}
                          {user.price ? ` ($${user.price.toFixed(2)})` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditFormResponsePage;
